/**
 * Prompt Library Utilities
 *
 * Uses Vite glob import to auto-discover all .md files in src/prompt-lib/
 * No manual index.json needed - just add/remove .md files and they're included on next build.
 */

import { ConnectorStatus, type ConnectorListItem } from '@optidev/shared-types';

// Vite glob import - auto-discovers all .md files at build time
const promptModules = import.meta.glob('/src/prompt-lib/**/*.md', {
  query: '?raw',
  eager: true,
});

export type StackType = 'frontend' | 'fullstack';

/**
 * Connector ids treated as available if the live connectors call hasn't returned or errored.
 * The live `status` from `useConnectors()` is authoritative; this is only a fail-safe so
 * genuinely-available prompts still show and coming-soon ones stay hidden on an API hiccup.
 */
export const FALLBACK_AVAILABLE_CONNECTORS = ['stripe', 'snowflake', 'openai', 'anthropic'];

/** Set of connector ids that are currently `available` (live status, with fail-safe fallback). */
export function getAvailableConnectorIds(connectors: ConnectorListItem[]): Set<string> {
  const live = connectors
    .filter((c) => c.status === ConnectorStatus.AVAILABLE)
    .map((c) => c.id);
  return new Set(live.length ? live : FALLBACK_AVAILABLE_CONNECTORS);
}

/** A prompt is visible if it has no connector, or its connector is currently available. */
export function isPromptAvailable(prompt: Pick<Prompt, 'connector'>, availableConnectorIds: Set<string>): boolean {
  return !prompt.connector || availableConnectorIds.has(prompt.connector);
}

export interface PromptFrontmatter {
  title: string;
  description: string;
  category: string;
  stackType: StackType;
  /** Optional one-line setup hint shown on the detail page (e.g. backend secrets to add) */
  setup?: string;
  /** Optional cover preview image (first of `previews`) shown on the detail page */
  preview?: string;
  /** Optional gallery of preview images — paged through in the lightbox */
  previews?: string[];
  /** Optional App Library app id — if set, the entry can be forked (remixed) exactly */
  remixAppId?: string;
  /** Optional connector id (e.g. 'stripe', 'snowflake'). The prompt is hidden unless that
   *  connector's live status is 'available' — see the list page's availability filter. */
  connector?: string;
}

export interface Prompt {
  id: string;
  path: string;
  title: string;
  description: string;
  category: string;
  stackType: StackType;
  setup?: string;
  preview?: string;
  previews?: string[];
  remixAppId?: string;
  connector?: string;
  content: string;
}

export interface CategoryConfig {
  id: string;
  label: string;
  order: number;
}

// Category display configuration
export const CATEGORIES: CategoryConfig[] = [
  { id: 'website-apps', label: 'Website & Apps', order: 1 },
  { id: 'dashboard-feeds', label: 'Dashboard & Feeds', order: 2 },
  { id: 'games', label: 'Games', order: 3 },
];

/**
 * Parse YAML frontmatter from markdown content
 */
export function parseFrontmatter(content: string): {
  frontmatter: PromptFrontmatter;
  body: string;
} {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return {
      frontmatter: { title: 'Untitled', description: '', category: 'other', stackType: 'frontend' },
      body: content,
    };
  }

  const [, frontmatterStr, body] = match;
  const frontmatter: PromptFrontmatter = {
    title: 'Untitled',
    description: '',
    category: 'other',
    stackType: 'frontend',
  };

  // Simple YAML parsing for our use case
  const lines = frontmatterStr.split('\n');
  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();

    if (key === 'title') frontmatter.title = value;
    if (key === 'description') frontmatter.description = value;
    if (key === 'category') frontmatter.category = value;
    if (key === 'setup' && value) frontmatter.setup = value;
    if (key === 'preview' && value) {
      // Comma-separated list — first entry is the cover, the rest extend the gallery
      const list = value.split(',').map((s) => s.trim()).filter(Boolean);
      frontmatter.previews = list;
      frontmatter.preview = list[0];
    }
    if (key === 'remixAppId' && value) frontmatter.remixAppId = value;
    if (key === 'connector' && value) frontmatter.connector = value;
    if (key === 'stackType' && (value === 'frontend' || value === 'fullstack')) {
      frontmatter.stackType = value;
    }
  }

  return { frontmatter, body };
}

/**
 * Extract the prompt text from markdown content.
 *
 * New format: the entire markdown body IS the prompt (no markers needed).
 * Legacy format: content wrapped between <!-- FINAL_PROMPT_START --> and
 * <!-- FINAL_PROMPT_END --> is still honored as a fallback.
 */
export function extractFinalPrompt(content: string): string {
  const startMarker = '<!-- FINAL_PROMPT_START -->';
  const endMarker = '<!-- FINAL_PROMPT_END -->';

  const startIndex = content.indexOf(startMarker);
  const endIndex = content.indexOf(endMarker);

  if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
    return content.slice(startIndex + startMarker.length, endIndex).trim();
  }

  // No markers — the whole body is the prompt.
  return content.trim();
}

/**
 * Get all prompts from the prompt-lib folder
 */
export function getAllPrompts(): Prompt[] {
  const validCategories = new Set(CATEGORIES.map((c) => c.id));

  return Object.entries(promptModules)
    // The glob also matches documentation living in the folder (e.g. prompt-lib/CLAUDE.md),
    // which has no `category` frontmatter. Without this filter such a file is treated as a
    // prompt and its empty category becomes a phantom group in getPromptsByCategory().
    .filter(([, module]) => {
      const { frontmatter } = parseFrontmatter((module as { default: string }).default);
      return validCategories.has(frontmatter.category);
    })
    .map(([path, module]) => {
      const content = (module as { default: string }).default;
      const { frontmatter, body } = parseFrontmatter(content);

      // Extract id from path: /src/prompt-lib/website-apps/product-showcase-kiosk.md -> product-showcase-kiosk
      const fileName = path.split('/').pop() || '';
      const id = fileName.replace('.md', '');

      return {
        id,
        path,
        title: frontmatter.title,
        description: frontmatter.description,
        category: frontmatter.category,
        stackType: frontmatter.stackType,
        setup: frontmatter.setup,
        preview: frontmatter.preview,
        previews: frontmatter.previews,
        remixAppId: frontmatter.remixAppId,
        connector: frontmatter.connector,
        content: body,
      };
    });
}

/**
 * Get prompts grouped by category
 */
export function getPromptsByCategory(): Map<string, Prompt[]> {
  const prompts = getAllPrompts();
  const grouped = new Map<string, Prompt[]>();

  // Initialize all categories
  for (const cat of CATEGORIES) {
    grouped.set(cat.id, []);
  }

  // Group prompts
  for (const prompt of prompts) {
    const existing = grouped.get(prompt.category) || [];
    existing.push(prompt);
    grouped.set(prompt.category, existing);
  }

  return grouped;
}

/**
 * Get a single prompt by ID
 */
export function getPromptById(id: string): Prompt | undefined {
  const prompts = getAllPrompts();
  return prompts.find((p) => p.id === id);
}

/**
 * Search prompts by query (searches title and description)
 */
export function searchPrompts(query: string): Prompt[] {
  if (!query.trim()) {
    return getAllPrompts();
  }

  const lowerQuery = query.toLowerCase();
  return getAllPrompts().filter(
    (prompt) =>
      prompt.title.toLowerCase().includes(lowerQuery) ||
      prompt.description.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Filter prompts by category
 */
export function filterPromptsByCategory(
  category: string | null
): Map<string, Prompt[]> {
  if (!category || category === 'all') {
    return getPromptsByCategory();
  }

  const prompts = getAllPrompts().filter((p) => p.category === category);
  const grouped = new Map<string, Prompt[]>();
  grouped.set(category, prompts);

  return grouped;
}
